<script setup lang="ts">
import "../../../../../../../apps/pay-app/static-src/js/components/pay-stripe-component.js";
import "../../../../../../../legl-ui/adyen-component/src/adyen-component.js";

import { onActivated, onBeforeMount, onBeforeUnmount } from "vue";
import LeglSpinner from "../../../../../../components/LeglSpinner.vue";
import { PaymentSources } from "../../../../../apps/pay/src/constants/payment-sources.js";
import { usePayStore } from "../../store";
import type { PaymentStep } from "../../types";
import PaymentSelector from "./PaymentSelector.vue";

const emit = defineEmits<{
  (e: "step:complete"): void;
  (e: "step:back"): void;
}>();

onBeforeMount(() => {
  window.addEventListener("payment-completed", () => emit("step:complete"));
});

onBeforeUnmount(() => {
  window.removeEventListener("payment-completed", () => emit("step:complete"));
});

onActivated(() => {
  if (payStore.availablePaymentMethods.length === 1) {
    payStore.handleMethodSelection(payStore.availablePaymentMethods[0]);
  }
});

defineProps<{
  currentStep: PaymentStep;
}>();

const payStore = usePayStore();

function handleUnselectPaymentMethod() {
  payStore.selectedPaymentMethod = null;
  payStore.providerDetails = null;
}

function handleStepBack() {
  emit("step:back");
}

const isCheckout =
  payStore.paymentSource === PaymentSources.CHECKOUT ||
  payStore.paymentSource === PaymentSources.CUSTOM_CHECKOUT;

// const isPaymentPlan = payStore.paymentSource === PaymentSources.PAYMENT_PLAN;

// const stepBackButtonText = isPaymentPlan
//   ? "Back to payment plan overview"
//   : "Back to transaction details";
</script>

<template>
  <div data-testid="payment-step" id="payment-step" v-if="payStore.paymentDetails?.providers.length">
    <lds-button
        v-if="payStore.selectedPaymentMethod && payStore.availablePaymentMethods.length > 1"
        colour="neutral"
        variant="text"
        icon="lds-icon-ChevronLeft"
        @click="handleUnselectPaymentMethod"
      >
        Back to payment methods
    </lds-button>
    <lds-button
        v-else-if="isCheckout"
        colour="neutral"
        variant="text"
        icon="lds-icon-ChevronLeft"
        class="back-to-transaction-details-button"
        @click="handleStepBack"
      >
      Back to transaction details
    </lds-button>
    <PaymentSelector v-if="!payStore.selectedPaymentMethod" />
    <div v-show="payStore.loadingProviderDetails" class="spinner-container">
      <LeglSpinner />
    </div>
    <div v-show="payStore.selectedPaymentMethod && ['stripe', 'adyen'].includes(payStore.selectedPaymentMethod.provider) && payStore.providerDetails">
      <h3>Pay by card</h3>
      <p>Enter your card details to make a secure payment to <b>{{ payStore.companyDetails?.name }}</b> within seconds.</p>
    </div>
    <div v-show="payStore.selectedPaymentMethod?.provider == 'banked' && payStore.providerDetails">
      <h3>Make a bank transfer</h3>
      <p>Connect with your bank directly to make a secure bank transfer to <b>{{ payStore.companyDetails?.name }}</b>.</p>
      <p>We pass all payment details on to your bank, so you can complete your bank transfer easily, securely and within seconds.</p>
    </div>
    <div v-if="payStore.providerDetails" id="payment-method-component"></div>
    <p v-show="payStore.selectedPaymentMethod?.provider == 'banked' && payStore.providerDetails"></p>
  </div>
  <p v-else class="no-payment-methods-available">
    No payment methods available, please contact support.
  </p>
</template>

<style scoped lang="scss">
.spinner-container {
  margin-top: $spacing-m;
  text-align: center;
}

#payment-step {
  @include typography-body;
  color: $color-neutral-900;
  b {
    @include typography-body-bold;
  }
  h3 {
    margin: 0 0 $spacing-m 0;
    @include typography-h3;
    color: $color-neutral-900;
  }
  a {
    color: $color-primary-500;
    text-decoration: underline;
  }
}
.no-payment-methods-available {
  @include typography-body;
  color: $color-neutral-800;
  margin-top: $spacing-m;
}
.back-to-transaction-details-button {
  margin-bottom: $spacing-m;
}
</style>
